import { DraggableItem } from "@/components/features/matter/file-repo/DraggableItem";
import { UploadFileReferenceFromStorageButton } from "@/components/features/matter/medical-treatments/UploadFileReferenceFromStorageButton";
import { ActionConfirmationModal, Typography } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMatterFileReference } from "@/lib/data/react-query/matters/matter-file";
import type { Matter, MatterFile } from "@/lib/types";
import type { MatterFileReferenceSchema } from "@/lib/data/schemas";
import type { ACTION_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { ACTION_PERMISSION_VALUES } from "@/lib/data/static/permissionOptions";
import { useState } from "react";

export type FileReferencesSectionFile = MatterFile & {
  fileReferenceId: string;
};

interface FileReferencesSectionProps {
  editing?: boolean;
  fileReferences: FileReferencesSectionFile[];
  matter: Matter;
  itemKey: keyof typeof MatterFileReferenceSchema.shape;
  itemId: string;
  permissionKey: (typeof ACTION_PERMISSION_KEYS)[keyof typeof ACTION_PERMISSION_KEYS];
  maxFiles?: number;
}

export const FileReferencesSection = ({
  editing,
  fileReferences,
  matter,
  itemKey,
  itemId,
  permissionKey,
  maxFiles,
}: FileReferencesSectionProps) => {
  const [fileReferenceToDelete, setFileReferenceToDelete] =
    useState<FileReferencesSectionFile | null>(null);
  const { hasActionPermission } = useUser();
  const { openFileModal } = useGlobalModal();
  const deleteMatterFileReference = useDeleteMatterFileReference();

  const canEditFileReferences =
    Boolean(editing) &&
    hasActionPermission(permissionKey, ACTION_PERMISSION_VALUES.update);

  return (
    <>
      <div className="col-span-full flex items-baseline gap-x-2">
        <Typography variant="heading">File references</Typography>
      </div>
      <div className="col-span-full mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {editing && (
          <>
            {fileReferences.map((fileReference) => (
              <DraggableItem
                key={fileReference.id}
                item={fileReference}
                isSelected={false}
                onSelect={() => {
                  openFileModal({
                    matterId: matter.id,
                    activeFileId: fileReference.id,
                    editing: true,
                    currentFolderId: fileReference.idLocation ?? "",
                    isFolder: Boolean(fileReference.isFolder),
                  });
                }}
                onDelete={() => {
                  setFileReferenceToDelete(fileReference);
                }}
              />
            ))}
          </>
        )}
        {(maxFiles === undefined || fileReferences.length < maxFiles) && (
          <UploadFileReferenceFromStorageButton
            matter={matter}
            itemKey={itemKey}
            itemId={itemId}
            disabled={!canEditFileReferences}
            disabledMessage="To interact with this section, please save first"
          />
        )}
        <ActionConfirmationModal
          open={Boolean(fileReferenceToDelete)}
          setOpen={(open) => {
            if (!open) {
              setFileReferenceToDelete(null);
            }
          }}
          title="Delete file reference?"
          description="Are you sure you want to delete this file reference? This will not delete the underlying file, but this action cannot be undone."
          buttonText="Delete"
          afterActionFn={async () => {
            if (!fileReferenceToDelete) return;
            await deleteMatterFileReference.mutateAsync({
              matterId: matter.id,
              id: fileReferenceToDelete.fileReferenceId,
            });
            setFileReferenceToDelete(null);
          }}
        />
      </div>
    </>
  );
};
